import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  Button,
  CustomAccordion,
  FormStateField,
  FormTextField,
  FormZipcodeField,
} from "@covertree/ui";

import { communityConfigurationCardSx } from "./communityConfigurationStyles";
import {
  makeCommunityConfigurationClientId,
  toStateOption,
} from "./communitiesConfigurationForm";

import type { CommunityConfiguration } from "@graphql/api";
import type {
  CommunityConfigurationFormOwner,
  CommunityConfigurationFormValues,
} from "./communitiesConfigurationForm";

interface OwnersSectionProps {
  portfolio: CommunityConfiguration["portfolio"];
}

const ownerFields: Array<{
  field: keyof Pick<
    CommunityConfigurationFormOwner,
    | "addressLine1"
    | "addressLine2"
    | "city"
    | "county"
    | "municipality"
    | "name"
    | "state"
    | "zipCode"
  >;
  label: string;
  requiredForActivation: boolean;
  CustomComponent?: React.ElementType;
}> = [
  { field: "name", label: "Legal name", requiredForActivation: true },
  {
    field: "addressLine1",
    label: "Address line 1",
    requiredForActivation: true,
  },
  {
    field: "addressLine2",
    label: "Address line 2",
    requiredForActivation: false,
  },
  { field: "city", label: "City", requiredForActivation: true },
  { field: "county", label: "County", requiredForActivation: true },
  {
    field: "municipality",
    label: "Municipality",
    requiredForActivation: false,
  },
  {
    field: "state",
    label: "State",
    requiredForActivation: true,
    CustomComponent: FormStateField,
  },
  {
    field: "zipCode",
    label: "ZIP code",
    requiredForActivation: true,
    CustomComponent: FormZipcodeField,
  },
];

export function OwnersSection({ portfolio }: OwnersSectionProps) {
  const { control } = useFormContext<CommunityConfigurationFormValues>();
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: "formKey",
    name: "owners",
  });
  const communities = useWatch({ control, name: "communities" }) ?? [];
  const owners = useWatch({ control, name: "owners" }) ?? [];

  return (
    <Stack sx={communityConfigurationCardSx} spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Legal entity community owners</Typography>
        <Button
          type="button"
          variant="outlined"
          onClick={() =>
            append({
              addressLine1: portfolio.addressLine1,
              addressLine2: portfolio.addressLine2 ?? "",
              city: portfolio.city,
              clientId: makeCommunityConfigurationClientId(),
              county: portfolio.county ?? "",
              municipality: "",
              name: portfolio.name,
              state: toStateOption(portfolio.state),
              zipCode: portfolio.zipCode,
            })
          }>
          Add owner
        </Button>
      </Stack>

      {fields.map((formOwner, ownerIndex) => {
        const owner = owners[ownerIndex];
        if (!owner) return null;
        const isReferenced = communities.some(
          ({ ownerClientId }) => ownerClientId === owner.clientId
        );
        const isActiveOwner = communities.some(
          ({ isActive, ownerClientId }) =>
            isActive && ownerClientId === owner.clientId
        );

        return (
          <CustomAccordion
            key={formOwner.formKey}
            expandOnInit={ownerIndex === 0}
            withoutAccent
            withoutBorder
            header={() => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={600}>
                  {owner.name || `Owner ${ownerIndex + 1}`}
                </Typography>
                {isReferenced && <Chip size="small" label="Assigned" />}
              </Stack>
            )}
            content={
              <>
                <Grid container spacing={2}>
                  {ownerFields.map(
                    ({
                      field: ownerField,
                      label,
                      requiredForActivation,
                      CustomComponent,
                    }) => (
                      <Grid item xs={12} md={6} key={ownerField}>
                        {CustomComponent ? (
                          <CustomComponent
                            control={control}
                            fullWidth
                            id={`owner-${ownerIndex}-${ownerField}`}
                            label={label}
                            name={`owners.${ownerIndex}.${ownerField}`}
                            required={isActiveOwner && requiredForActivation}
                          />
                        ) : (
                          <FormTextField
                            control={control}
                            fullWidth
                            id={`owner-${ownerIndex}-${ownerField}`}
                            label={label}
                            name={`owners.${ownerIndex}.${ownerField}`}
                            required={isActiveOwner && requiredForActivation}
                          />
                        )}
                      </Grid>
                    )
                  )}
                </Grid>
                <Stack direction="row" spacing={1} mt={2}>
                  <Button
                    type="button"
                    onClick={() =>
                      append({
                        ...owner,
                        clientId: makeCommunityConfigurationClientId(),
                        id: null,
                      })
                    }>
                    Duplicate for another locality
                  </Button>
                  <Button
                    color="error"
                    disabled={isReferenced || fields.length === 1}
                    type="button"
                    onClick={() => remove(ownerIndex)}>
                    Remove
                  </Button>
                </Stack>
              </>
            }
          />
        );
      })}
    </Stack>
  );
}
